import { useMutation } from "@apollo/client";
import { Box, Checkbox, CircularProgress, FormControlLabel, Stack, TextField } from "@mui/material";
import { gql } from "generated-graphql";
import { ActivityUserInput as ActivityUserInputGql } from "generated-graphql/graphql";
import { useDebounce } from "hooks/useDebounce";
import { useEffect, useRef, useState } from "react";

import { useAlerts } from "../../../../../components/Alerts/AlertProvider";
import { isPlainNumber } from "./activityInputValidation";

const UPDATE_ACTIVITY_INPUT_MUTATION = gql(`
  mutation SetValueOnActivityUserInput($id: ID!, $value: String!) {
    setValueOnActivityUserInput(id: $id, value: $value) {
      id
      value
}}`);

type Props = {
  feeInput: ActivityUserInputGql;
  noFeeInput: ActivityUserInputGql;
  disabled: boolean;
};

/**
 * Renders the lobFee amount field and the "No fee required" checkbox as a coupled
 * pair. The server rejects (lobFee > 0 AND noLobFee = true), so checking the box
 * must clear the fee FIRST, then set the box.
 *
 * Because the two inputs persist through independent HTTP requests with no
 * server-side ordering guarantee, ALL writes flow through one sequential queue:
 * each mutation dispatches only after every earlier one has settled, so the
 * server always receives writes in user-intent order (a slow fee write can never
 * land inside the clear-then-set sequence, and rapid toggling resolves to the
 * last click).
 *
 * Fee-field keystrokes are debounced (1000ms) to match the generic ActivityUserInput
 * pattern so typing "1500" fires a single mutation rather than one per keystroke.
 *
 * A persist failure surfaces an alert (the server's message is shown alongside)
 * and reverts the optimistic local state - but only when nothing newer happened:
 * each keystroke, toggle, and dispatched write bumps a generation counter, and a
 * failed write only reverts if its generation is still current, so a stale
 * failure can never clobber newer user intent.
 */
export const LobFeeInputs = ({ feeInput, noFeeInput, disabled }: Props) => {
  const alerts = useAlerts();
  const [setInput, { loading }] = useMutation(UPDATE_ACTIVITY_INPUT_MUTATION);

  const [fee, setFee] = useState(feeInput.value ?? "");
  const [noFee, setNoFee] = useState(noFeeInput.value === "true");
  const [debouncedFee] = useDebounce(fee, 1000);

  // Sequential write queue: jobs handle their own errors, so the chain never
  // rejects and every job runs after all earlier ones settle.
  const writeQueueRef = useRef<Promise<void>>(Promise.resolve());
  const enqueueWrite = (job: () => Promise<void>) => {
    writeQueueRef.current = writeQueueRef.current.then(job);
  };

  // Bumped on every user intent (fee keystroke, toggle click, dispatched write).
  // Failure handlers only revert local state when their generation is still
  // current.
  const writeGenRef = useRef(0);

  // The last fee value handed to the queue (dedupes the debounced effect) and the
  // last fee value the server confirmed (the revert target on failure).
  const dispatchedFeeRef = useRef(feeInput.value ?? "");
  const confirmedFeeRef = useRef(feeInput.value ?? "");

  useEffect(() => {
    // Do not persist debounced keystrokes while the no-fee box is checked: the fee
    // is forced to "" + disabled, and a stale write here could trip the resolver
    // guard.
    if (noFee) {
      return;
    }
    if (debouncedFee === dispatchedFeeRef.current) {
      return;
    }
    // Never persist a value the field is flagging as invalid: downstream parsing
    // strips commas, so e.g. "12,50" would silently become a $1250 check.
    if (debouncedFee && !isPlainNumber(debouncedFee)) {
      return;
    }
    const gen = ++writeGenRef.current;
    dispatchedFeeRef.current = debouncedFee;
    enqueueWrite(async () => {
      try {
        await setInput({ variables: { id: feeInput.id, value: debouncedFee } });
        confirmedFeeRef.current = debouncedFee;
      } catch (e) {
        if (gen === writeGenRef.current) {
          dispatchedFeeRef.current = confirmedFeeRef.current;
          setFee(confirmedFeeRef.current);
        }
        alerts.error("Failed to save the fee amount.", e);
      }
    });
  }, [debouncedFee, noFee, feeInput.id, setInput, alerts]);

  const onToggleNoFee = (checked: boolean) => {
    const gen = ++writeGenRef.current;
    if (checked) {
      // Optimistic local state first: noFee=true also stops the debounced effect
      // from dispatching further fee writes while the sequence below is queued.
      setNoFee(true);
      setFee("");
      dispatchedFeeRef.current = "";
      enqueueWrite(async () => {
        try {
          // Clear the fee before setting the box so the resolver never sees a
          // conflict.
          await setInput({ variables: { id: feeInput.id, value: "" } });
          confirmedFeeRef.current = "";
        } catch (e) {
          if (gen === writeGenRef.current) {
            setNoFee(false);
            setFee(confirmedFeeRef.current);
            dispatchedFeeRef.current = confirmedFeeRef.current;
          }
          alerts.error("Failed to update the no-fee designation.", e);
          return;
        }
        try {
          await setInput({ variables: { id: noFeeInput.id, value: "true" } });
        } catch (e) {
          // The fee clear already succeeded; only the designation failed, so leave
          // the fee field empty (matching the server) and revert just the checkbox.
          if (gen === writeGenRef.current) {
            setNoFee(false);
          }
          alerts.error("Failed to update the no-fee designation.", e);
        }
      });
    } else {
      setNoFee(false);
      enqueueWrite(async () => {
        try {
          await setInput({ variables: { id: noFeeInput.id, value: "false" } });
        } catch (e) {
          // The server still has the check auto-skipping; the box must not render
          // unchecked as if a fee will be charged.
          if (gen === writeGenRef.current) {
            setNoFee(true);
          }
          alerts.error("Failed to update the no-fee designation.", e);
        }
      });
    }
  };

  const feeError = !!fee && !isPlainNumber(fee);

  return (
    <Stack sx={{ my: 1 }}>
      <TextField
        label={feeInput.label}
        value={fee}
        disabled={disabled || noFee}
        required={false}
        onChange={(e) => {
          writeGenRef.current++;
          setFee(e.target.value);
        }}
        error={feeError}
        helperText={
          feeError ? "Value must be a plain number without commas, spaces, or symbols." : ""
        }
      />
      <Stack direction="row" alignItems="center">
        <FormControlLabel
          control={
            <Checkbox
              disabled={disabled}
              checked={noFee}
              onChange={(e) => onToggleNoFee(e.target.checked)}
            />
          }
          label={noFeeInput.label}
        />
        {loading && <CircularProgress size={20} />}
        <Box sx={{ width: 20 }} />
      </Stack>
    </Stack>
  );
};
